// Form helpers (copied from xenquote components/controls.tsx: Section, Field, Segmented, Toggle)
// styled by editor.css.
import type { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import "../editor.css";

/** Collapsible group of related settings. */
export function Section({ title, children, defaultOpen = true }: { title: string; children: ReactNode; defaultOpen?: boolean }) {
  return (
    <details className="ed-section" open={defaultOpen}>
      <summary>
        {title}
        <ChevronDown size={16} aria-hidden="true" />
      </summary>
      <div className="ed-section-body">{children}</div>
    </details>
  );
}

/** Label + control (+ optional hint) on one row or stacked. */
export function Field({ label, htmlFor, hint, children }: { label: string; htmlFor?: string; hint?: string; children: ReactNode }) {
  return (
    <div className="ed-field">
      <label className="ed-label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {hint && <p className="ed-hint">{hint}</p>}
    </div>
  );
}

export function Segmented<T extends string>({ value, options, onChange }: { value: T; options: [T, string][]; onChange: (v: T) => void }) {
  return (
    <div className="ed-segmented" role="radiogroup">
      {options.map(([v, label]) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} className={value === v ? "is-on" : ""} onClick={() => onChange(v)}>
          {label}
        </button>
      ))}
    </div>
  );
}

/** On/off switch. Pass ariaLabel instead of label for a switch with no visible text (e.g. in a table). */
export function Toggle({
  id,
  checked,
  label,
  ariaLabel,
  onChange,
}: {
  id: string;
  checked: boolean;
  label?: string;
  ariaLabel?: string;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="ed-toggle" htmlFor={id}>
      <input id={id} type="checkbox" role="switch" aria-label={ariaLabel} checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="ed-toggle-track" aria-hidden="true" />
      {label}
    </label>
  );
}
